// Dashboard de inicio compartido por recepcionista, médico y admin (ruta
// /dashboard-recepcionista; /dashboard-medico redirige acá) — pedido
// explícito del usuario 2026-11-02: en consultorios chicos cualquiera
// cubre de recepcionista de cualquier médico, así que la página no debe
// distinguir rol (misma sala de espera, mismo check-in, misma agenda del
// día). La única diferencia real es "Llamar paciente e iniciar consulta"
// en la sala de espera (ver WaitingRoom.js), visible para cualquiera que
// pueda gestionar la cola. El nombre del archivo quedó igual para no
// generar un diff enorme solo por semántica — antes vivía también
// MedicalDashboard.js con una vista separada y más angosta (sin agenda
// del día ni cambio de estado inline), eliminado en este mismo cambio.
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, Paper, Button,
  TextField, InputAdornment, Grid, IconButton, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Divider, Skeleton, Tooltip, Checkbox,
} from '@mui/material';
import {
  Search, Add, AccessTime, Person, CheckCircle, Cancel, Warning,
  HowToReg, ExpandMore, InfoOutlined,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useClinicFeatures } from '../hooks/useClinicFeatures';
import { useNotification } from '../hooks/useNotification';
import { getAppointments, getPatients, updateAppointment, checkInAppointment } from '../services/api';
import { useNavigate } from 'react-router-dom';
import WaitingRoom from '../components/WaitingRoom';
import { APPOINTMENT_STATE_CONFIG } from '../constants/appointmentStates';
import { BannerInstalarApp } from '../components/InstalarApp';
import LinkIcon from '@mui/icons-material/Link';
import SelfUpdateLinkDialog from '../components/SelfUpdateLinkDialog';

// Mismo color hex que el calendario y /turnos (APPOINTMENT_STATE_CONFIG) —
// antes este archivo tenía su propia paleta de nombres MUI genéricos
// ('success', 'warning') que hacía que "Confirmado" se viera verde acá pero
// ámbar en el resto de la app, para el mismo dato.
const STATE_ICONS = {
  pendiente: <Warning fontSize="small" />,
  confirmado: <CheckCircle fontSize="small" />,
  cancelado: <Cancel fontSize="small" />,
  ausente: <Warning fontSize="small" />,
  atendido: <CheckCircle fontSize="small" />,
  libre: <AccessTime fontSize="small" />,
  ocupado: <Warning fontSize="small" />,
};

const STATE_OPTIONS = ['pendiente', 'confirmado', 'ausente', 'atendido', 'cancelado'].map(value => ({
  value,
  color: APPOINTMENT_STATE_CONFIG[value]?.color,
  label: APPOINTMENT_STATE_CONFIG[value]?.label ?? value,
}));

const STATE_CONFIG = Object.fromEntries(
  Object.keys(STATE_ICONS).map(k => [k, {
    color: APPOINTMENT_STATE_CONFIG[k]?.color ?? '#78909c',
    icon: STATE_ICONS[k],
    label: APPOINTMENT_STATE_CONFIG[k]?.label ?? k,
  }])
);

const TERMINAL_STATES = ['atendido', 'cancelado', 'ausente'];

const RecepcionistDashboard = () => {
  const { user, clinicId } = useAuth();
  const { features } = useClinicFeatures();
  const navigate = useNavigate();
  const notify = useNotification();
  const [loading, setLoading] = useState(true);
  // "Crear paciente por link" también en Inicio, no solo en Pacientes —
  // pedido explícito del usuario (2026-10-20), mismo diálogo compartido.
  const [newPatientLinkOpen, setNewPatientLinkOpen] = useState(false);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [stateMenuAnchor, setStateMenuAnchor] = useState(null);
  const [stateMenuAppt, setStateMenuAppt] = useState(null);
  const [changingState, setChangingState] = useState(null);

  // Check-in dialog
  const [checkinAppt, setCheckinAppt] = useState(null);
  const [checkinNote, setCheckinNote] = useState('');
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [waitingRoomKey, setWaitingRoomKey] = useState(0);

  // Cancellation reason dialog
  const [cancelAppt, setCancelAppt] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  // Cancelación en lote — pedido explícito del usuario: antes cada turno
  // se cancelaba de a uno, y para una mañana con un médico que no viene
  // había que entrar turno por turno. Reprogramar/mover en lote queda
  // afuera por ahora (recalcular horarios y validar disponibilidad por
  // cada turno es mucho más trabajo) — solo cancelar, que es el caso real
  // más común de "hay que tocar varios turnos a la vez".
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkCancelOpen, setBulkCancelOpen] = useState(false);
  const [bulkCancelReason, setBulkCancelReason] = useState('');
  const [bulkCancelling, setBulkCancelling] = useState(false);

  const toggleSelect = (id) => setSelectedIds(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const selectableAppts = todayAppointments.filter(a => !TERMINAL_STATES.includes(a.state));
  const allSelected = selectableAppts.length > 0 && selectableAppts.every(a => selectedIds.includes(a.id));
  const toggleSelectAll = () => setSelectedIds(allSelected ? [] : selectableAppts.map(a => a.id));

  const confirmBulkCancel = async () => {
    setBulkCancelling(true);
    // Filtrado contra el estado actual, no contra lo que era selectedIds
    // cuando se tildaron los checkboxes — si algo cambió de estado por otro
    // lado mientras el diálogo estaba abierto (otro recepcionista, un
    // médico marcando atendido), ya no corresponde cancelarlo acá.
    const ids = selectedIds.filter(id => {
      const appt = todayAppointments.find(a => a.id === id);
      return appt && !TERMINAL_STATES.includes(appt.state);
    });
    const reason = bulkCancelReason;
    const succeededIds = [];
    for (const id of ids) {
      const appt = todayAppointments.find(a => a.id === id);
      try {
        await updateAppointment(id, {
          state: 'cancelado',
          start_time: new Date(appt.start_time + 'Z').toISOString(),
          duration_minutes: appt.duration_minutes,
          cancellation_reason: reason || null,
        });
        succeededIds.push(id);
      } catch { /* se sigue con el resto, se informa el total al final */ }
    }
    // Solo se marcan como cancelados en el estado local los que realmente
    // lo lograron en el backend — antes esto marcaba TODOS los
    // seleccionados sin importar si la llamada individual había fallado,
    // dejando filas en pantalla como "cancelado" cuando en el backend
    // seguían con su estado real (desincronización real de datos).
    setTodayAppointments(prev => prev.map(a => succeededIds.includes(a.id) ? { ...a, state: 'cancelado' } : a));
    setSelectedIds([]);
    setBulkCancelOpen(false);
    setBulkCancelReason('');
    setBulkCancelling(false);
    if (succeededIds.length === ids.length) {
      notify(`${succeededIds.length} turno${succeededIds.length === 1 ? '' : 's'} cancelado${succeededIds.length === 1 ? '' : 's'}`, 'success');
    } else {
      notify(`${succeededIds.length} de ${ids.length} turnos cancelados — reintentá los que fallaron`, 'warning');
    }
  };

  const fetchToday = useCallback(async () => {
    if (!clinicId) { setLoading(false); return; }
    try {
      const today = new Date();
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString();
      const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1).toISOString();
      const response = await getAppointments({ start_date: startOfDay, end_date: endOfDay, limit: 200 });
      setTodayAppointments(response.data?.items || []);
    } catch {
      // silently fail — not critical
    } finally {
      setLoading(false);
    }
  }, [clinicId]);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  useEffect(() => {
    const searchPatients = async () => {
      if (searchQuery.length < 2) { setSearchResults([]); return; }
      try {
        const response = await getPatients({ search: searchQuery, limit: 10 });
        setSearchResults(response.data?.items || []);
      } catch { /* ignore */ }
    };
    const timeout = setTimeout(searchPatients, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const openStateMenu = (e, appt) => {
    if (!appt.patient_id) return;
    e.stopPropagation();
    setStateMenuAnchor(e.currentTarget);
    setStateMenuAppt(appt);
  };
  const closeStateMenu = () => { setStateMenuAnchor(null); setStateMenuAppt(null); };

  const applyStateChange = async (appt, newState, cancellationReason = null) => {
    setChangingState(appt.id);
    try {
      await updateAppointment(appt.id, {
        state: newState,
        start_time: new Date(appt.start_time + 'Z').toISOString(),
        duration_minutes: appt.duration_minutes,
        ...(newState === 'cancelado' ? { cancellation_reason: cancellationReason || null } : {}),
      });
      setTodayAppointments(prev =>
        prev.map(a => a.id === appt.id ? { ...a, state: newState } : a)
      );
      notify(`Estado actualizado a ${STATE_CONFIG[newState]?.label ?? newState}`, 'success');
    } catch (err) {
      notify(err.userMessage || 'Error al cambiar estado', 'error');
    } finally {
      setChangingState(null);
    }
  };

  const handleStateChange = (newState) => {
    const appt = stateMenuAppt;
    closeStateMenu();
    if (newState === 'cancelado') {
      setCancelAppt(appt);
      setCancelReason('');
      return;
    }
    applyStateChange(appt, newState);
  };

  const confirmCancellation = async () => {
    if (!cancelAppt) return;
    const appt = cancelAppt;
    const reason = cancelReason;
    setCancelAppt(null);
    setCancelReason('');
    await applyStateChange(appt, 'cancelado', reason);
  };

  const openCheckinDialog = (appt) => {
    setCheckinAppt(appt);
    setCheckinNote('');
  };

  const handleCheckin = async () => {
    if (!checkinAppt) return;
    setCheckinLoading(true);
    try {
      await checkInAppointment(checkinAppt.id, { reception_note: checkinNote || null });
      notify(`${checkinAppt.patient?.name} recepcionado`, 'success');
      setCheckinAppt(null);
      setCheckinNote('');
      // Update local state to mark checked in
      setTodayAppointments(prev =>
        prev.map(a => a.id === checkinAppt.id
          ? { ...a, checked_in_at: new Date().toISOString(), state: a.state === 'pendiente' ? 'confirmado' : a.state }
          : a
        )
      );
      setWaitingRoomKey(k => k + 1);
    } catch (err) {
      notify(err.userMessage || 'Error al recepcionar paciente', 'error');
    } finally {
      setCheckinLoading(false);
    }
  };

  const confirmedCount = todayAppointments.filter(a => a.state === 'confirmado').length;
  const pendingCount   = todayAppointments.filter(a => a.state === 'pendiente' || a.state === 'ocupado').length;
  const checkedInCount = todayAppointments.filter(a => a.checked_in_at && !TERMINAL_STATES.includes(a.state)).length;

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ flex: 1, maxWidth: 260 }}>
            <Skeleton variant="text" width="70%" height={36} />
            <Skeleton variant="text" width="90%" />
          </Box>
          <Skeleton variant="rounded" width={140} height={36} />
        </Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[0, 1, 2, 3].map(i => (
            <Grid size={{ xs: 6, sm: 3 }} key={i}>
              <Skeleton variant="rounded" height={76} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={280} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Hola, {user?.username}</Typography>
          <Typography variant="body2" color="text.secondary">
            {new Date().toLocaleDateString('es-AR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>
        </Box>
        {/* Mobile: solo ícono — mismo criterio ya aplicado en la Agenda
            (Dashboard.js), reportado de nuevo acá 2026-11-02 ("los botones
            están muy grandes"). */}
        <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
          {features?.patient_self_update && (
            <Tooltip title="Crear paciente por link">
              <Button
                variant="outlined" startIcon={<LinkIcon />}
                onClick={() => setNewPatientLinkOpen(true)}
                sx={{ minWidth: { xs: 0, sm: 'auto' }, px: { xs: 1, sm: 2 }, '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } } }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Crear paciente por link</Box>
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Nuevo turno">
            <Button
              variant="contained" startIcon={<Add />}
              onClick={() => navigate('/agenda')}
              sx={{ minWidth: { xs: 0, sm: 'auto' }, px: { xs: 1, sm: 2 }, '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } } }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Nuevo turno</Box>
            </Button>
          </Tooltip>
        </Box>
      </Box>

      <BannerInstalarApp />

      {/* Métricas */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? '#12293d' : '#e3f2fd', borderRadius: 2 }}>
            <CardContent sx={{ py: 2, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={600}>{todayAppointments.length}</Typography>
              <Typography variant="caption" color="text.secondary">Total hoy</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card sx={{ bgcolor: 'success.light', borderRadius: 2 }}>
            <CardContent sx={{ py: 2, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={600} color="success.main">{confirmedCount}</Typography>
              <Typography variant="caption" color="text.secondary">Confirmados</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          {/* Clickeable: lleva a /turnos ya filtrado por pendientes, sin
              límite hacia atrás en el tiempo — el número de esta tarjeta
              solo cuenta los de HOY, pero el listado al que lleva el click
              trae pendientes de cualquier fecha pasada (para no perder de
              vista uno viejo sin confirmar/cancelar). Antes ese desfasaje
              entre "lo que se ve" y "lo que trae el click" no se avisaba en
              ningún lado — se aclara acá con un ícono + tooltip. */}
          <Tooltip title="El listado incluye además turnos pendientes de días anteriores, no solo los de hoy" arrow>
            <Card
              onClick={() => navigate('/turnos', { state: { initialStateFilter: { state: 'pendiente', startDate: '2000-01-01' } } })}
              sx={{ bgcolor: 'warning.light', borderRadius: 2, cursor: 'pointer', '&:hover': { filter: 'brightness(0.97)' } }}
            >
              <CardContent sx={{ py: 2, textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={600} color="warning.main">{pendingCount}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                  Pendientes <InfoOutlined sx={{ fontSize: 12 }} />
                </Typography>
              </CardContent>
            </Card>
          </Tooltip>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card sx={{ bgcolor: (theme) => checkedInCount > 0
            ? (theme.palette.mode === 'dark' ? '#241f47' : '#e8eaf6')
            : theme.palette.grey[100], borderRadius: 2 }}>
            <CardContent sx={{ py: 2, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={600} color={checkedInCount > 0 ? 'primary.main' : 'text.secondary'}>
                {checkedInCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">En sala de espera</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sala de espera — misma página y mismas acciones para recepcionista,
          médico y admin (pedido explícito del usuario 2026-11-02: en
          consultorios chicos cualquiera hace de recepcionista de cualquier
          médico, así que la vista no debe distinguir rol). "Llamar
          paciente" abre la ficha con el contexto para cargar la consulta
          directamente — mismo destino que ya usaba MedicalDashboard. */}
      {features?.waiting_room && (
        <Paper elevation={1} sx={{ mb: 3, p: 2, borderRadius: 2 }}>
          <WaitingRoom
            key={waitingRoomKey}
            onCallPatient={(appt) => navigate(`/patients/${appt.patient_id}`, {
              state: {
                newRecordAppointmentId: appt.id,
                newRecordDoctorId: appt.doctor_id,
                newRecordAppointmentNote: appt.notes || appt.reception_note || null,
              },
            })}
          />
        </Paper>
      )}

      {/* Búsqueda de pacientes */}
      <Paper elevation={1} sx={{ mb: 3, p: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Buscar paciente por nombre, teléfono o DNI..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setShowSearch(true); }}
            size="small"
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><Search color="action" /></InputAdornment>
              ),
            }}
          />
          <Button variant="outlined" onClick={() => navigate('/patients')}>Ver todos</Button>
        </Box>
        {showSearch && searchResults.length > 0 && (
          <Box sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
            {searchResults.map((patient) => (
              <Box key={patient.id} sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                p: 1, borderBottom: '1px solid', borderColor: 'divider',
              }}>
                <Box sx={{ cursor: 'pointer', flex: 1, minWidth: 0 }} onClick={() => navigate(`/patients/${patient.id}`)}>
                  <Typography fontWeight={500}>{patient.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {patient.phone} {patient.dni ? `| DNI: ${patient.dni}` : ''}
                  </Typography>
                </Box>
                {/* "¿tiene turno hoy?" / "sacale un turno ya" es el caso de
                    uso más común al buscar desde acá — antes el único
                    destino posible era la ficha del paciente, y había que
                    ir a /agenda y volver a escribir el nombre a mano en el
                    buscador del diálogo de turno. No existe hoy forma de
                    preseleccionar el paciente al abrir la agenda (eso
                    requeriría tocar Dashboard/AppointmentDialog/
                    CalendarGrid) — como atajo rápido, se copia el nombre al
                    portapapeles para pegarlo directo ahí. */}
                <Button
                  size="small" variant="outlined" startIcon={<Add fontSize="small" />}
                  sx={{ flexShrink: 0, ml: 1 }}
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      await navigator.clipboard.writeText(patient.name);
                      notify(`"${patient.name}" copiado — pegalo en el buscador de paciente del turno`, 'info');
                    } catch {
                      // Puede fallar por permisos del navegador o contexto
                      // no seguro (HTTP) — antes se mostraba "copiado"
                      // igual, lo cual es engañoso: el usuario pegaría
                      // contenido viejo del portapapeles sin saber por qué.
                      notify(`Buscá "${patient.name}" en el buscador de paciente del turno`, 'info');
                    }
                    navigate('/agenda');
                  }}
                >
                  Turno
                </Button>
                <IconButton size="small" onClick={() => navigate(`/patients/${patient.id}`)} title="Ver ficha">
                  <Person />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* Agenda del día. overflow:hidden acá — el fix anterior (maxWidth:100%
          solo en el TableContainer) no alcanzaba: el Paper en sí no tenía
          ancho acotado contra el viewport, así que podía seguir creciendo
          para acomodar la tabla en vez de forzar el scroll interno.
          overflow:hidden en el padre + overflow-x:auto nativo de
          TableContainer es el patrón real para contener esto. Reportado
          de nuevo con captura 2026-11-02 (agregar la columna Tipo hizo la
          tabla todavía más ancha). */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #eee', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="h6" fontWeight={500}>Agenda del día</Typography>
          {selectedIds.length > 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">{selectedIds.length} seleccionado{selectedIds.length === 1 ? '' : 's'}</Typography>
              <Button size="small" color="error" variant="outlined" startIcon={<Cancel fontSize="small" />}
                onClick={() => { setBulkCancelReason(''); setBulkCancelOpen(true); }}>
                Cancelar seleccionados
              </Button>
              <Button size="small" onClick={() => setSelectedIds([])}>Deseleccionar</Button>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              <HowToReg sx={{ fontSize: 14, verticalAlign: 'middle' }} /> para recepcionar (confirma el turno) · flecha para otros estados
            </Typography>
          )}
        </Box>
        {todayAppointments.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography>No hay turnos para hoy</Typography>
          </Box>
        ) : (
          // Antes maxHeight: 440 forzaba un scroll interno chico (aislado
          // del scroll de la página) — con muchos turnos del día esto es
          // justo el patrón que más fricciona en mobile: el gesto táctil
          // suele "escapar" al scroll de la página en vez de entrar al
          // scroll interno de la tabla, y el usuario termina sintiendo que
          // "no scrollea". Pedido explícito del usuario. Sin maxHeight la
          // tabla crece con la página (un solo scroll, el del navegador);
          // stickyHeader sigue fijando el encabezado mientras se recorre
          // esa sección. maxWidth:100% — sin esto, en mobile la tabla (más
          // ancha que la pantalla por sus columnas) empujaba el ancho de
          // TODA la página en vez de quedar su overflow-x:auto contenido
          // acá adentro, y el usuario no podía scrollear ni ver la columna
          // Estado completa (reportado con captura 2026-11-02).
          // -webkit-overflow-scrolling: touch — sin esto, iOS Safari (y
          // Chrome en iOS, que usa el mismo motor WebKit) no siempre
          // habilita el swipe táctil para hacer scroll horizontal dentro
          // de un contenedor con overflow-x:auto anidado dentro de otros
          // contenedores con scroll propio (la página, el <main>); el
          // navegador prioriza el scroll vertical de la página y el gesto
          // nunca llega al contenedor interno. Bug clásico de WebKit,
          // reportado de nuevo 2026-11-02 (persistía después de los fixes
          // de overflow anteriores, probado en iPhone real y Chrome
          // DevTools con emulación iPhone).
          <TableContainer sx={{ maxWidth: '100%', WebkitOverflowScrolling: 'touch', overflowX: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={allSelected} indeterminate={selectedIds.length > 0 && !allSelected}
                      onChange={toggleSelectAll} disabled={selectableAppts.length === 0} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Hora</TableCell>
                  <TableCell sx={{ fontWeight: 600, display: { xs: 'none', sm: 'table-cell' } }}>Médico</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Paciente</TableCell>
                  <TableCell sx={{ fontWeight: 600, display: { xs: 'none', sm: 'table-cell' }, maxWidth: 110 }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...todayAppointments]
                  .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
                  .map((appt) => {
                    const cfg = STATE_CONFIG[appt.state] ?? STATE_CONFIG.pendiente;
                    const isChanging = changingState === appt.id;
                    const isSelectable = !TERMINAL_STATES.includes(appt.state);
                    const isCheckedIn = !!appt.checked_in_at && !TERMINAL_STATES.includes(appt.state);
                    // El check-in requiere el feature flag waiting_room en
                    // el backend (check_feature) — antes este botón se
                    // mostraba igual con el flag apagado y el click fallaba
                    // con un error del backend sin explicación.
                    const canCheckin = features?.waiting_room && appt.patient_id && !TERMINAL_STATES.includes(appt.state) && !appt.checked_in_at;
                    return (
                      <TableRow key={appt.id} hover selected={selectedIds.includes(appt.id)}
                        sx={isCheckedIn ? { bgcolor: (theme) => theme.palette.mode === 'dark' ? '#1a1f42' : '#f3f4ff' } : {}}>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={selectedIds.includes(appt.id)} disabled={!isSelectable}
                            onChange={() => toggleSelect(appt.id)} />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {new Date(appt.start_time + 'Z').toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{appt.doctor?.name || '-'}</TableCell>
                        <TableCell>
                          {appt.patient ? (
                            <Typography
                              variant="body2"
                              sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                              onClick={() => navigate(`/patients/${appt.patient_id}`)}
                            >
                              {appt.patient.name}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled" fontStyle="italic">Libre</Typography>
                          )}
                        </TableCell>
                        {/* Tipo de turno en su propia columna — pedido
                            explícito del usuario 2026-11-02, con noWrap +
                            maxWidth (ver header) para acotar nombres largos
                            en vez de romper el layout de la tabla. */}
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, maxWidth: 110 }}>
                          {appt.appointment_type ? (
                            <Tooltip title={appt.appointment_type.name}>
                              <Typography
                                variant="caption"
                                noWrap
                                sx={{ display: 'block', color: appt.appointment_type.color, fontWeight: 700 }}
                              >
                                {appt.appointment_type.name}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {/* Antes "llegó el paciente" eran dos interacciones
                              separadas: un ícono de check-in en otra columna
                              (que de paso promovía pendiente→confirmado del
                              lado del backend, sin decirlo) y un Chip acá
                              para cambiar el estado a mano — dos componentes
                              para lo que el recepcionista vive como un solo
                              paso. Ahora, si el turno puede recepcionarse,
                              ese es el botón primario de la fila (ya dice
                              "Confirmar llegada" cuando el turno todavía
                              está pendiente, dejando explícito que también
                              confirma) — el cambio de estado manual sigue
                              disponible como flecha secundaria al lado, para
                              casos distintos (confirmar por teléfono sin que
                              el paciente haya llegado, marcar ausente, etc). */}
                          {isChanging ? (
                            <CircularProgress size={18} />
                          ) : isCheckedIn ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {/* Mismo estilo "fuerte" (fondo sólido + ícono
                                  blanco) que el resto de los chips de estado
                                  más abajo — antes era outlined, se veía
                                  plano/desentonado al lado de los demás.
                                  Color propio (no uno de AppointmentState):
                                  "en espera" es un estado derivado del
                                  check-in, no del enum. Reportado 2026-11-02. */}
                              <Chip
                                label="En espera"
                                size="small"
                                icon={<AccessTime fontSize="small" />}
                                sx={{
                                  bgcolor: '#5c6bc0', color: 'white', fontWeight: 700,
                                  '& .MuiChip-icon': { color: 'white' },
                                }}
                              />
                              <IconButton size="small" onClick={(e) => openStateMenu(e, appt)} title="Cambiar estado">
                                <ExpandMore fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : canCheckin ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {/* Mobile: solo ícono — el texto completo
                                  ("Confirmar llegada"/"Recepcionar") hacía
                                  que la tabla se desbordara del viewport en
                                  mobile, y el scroll horizontal para verlo
                                  no funcionaba de forma confiable en todos
                                  los navegadores/dispositivos (reportado en
                                  iPhone real y Chrome DevTools, 2026-11-02).
                                  Preferible que la fila entre completa sin
                                  depender del swipe. */}
                              <Tooltip title={appt.state === 'pendiente' ? 'Confirmar llegada' : 'Recepcionar'}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => openCheckinDialog(appt)}
                                  startIcon={<HowToReg fontSize="small" />}
                                  sx={{
                                    whiteSpace: 'nowrap',
                                    minWidth: { xs: 0, sm: 'auto' },
                                    px: { xs: 1, sm: 2 },
                                    '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
                                  }}
                                >
                                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                    {appt.state === 'pendiente' ? 'Confirmar llegada' : 'Recepcionar'}
                                  </Box>
                                </Button>
                              </Tooltip>
                              <IconButton size="small" onClick={(e) => openStateMenu(e, appt)} title="Cambiar estado">
                                <ExpandMore fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Chip
                              label={cfg.label}
                              size="small"
                              icon={cfg.icon}
                              onClick={appt.patient_id ? (e) => openStateMenu(e, appt) : undefined}
                              sx={{
                                bgcolor: cfg.color, color: 'white', fontWeight: 700,
                                '& .MuiChip-icon': { color: 'white' },
                                ...(appt.patient_id ? { cursor: 'pointer' } : {}),
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* State change menu */}
      <Menu anchorEl={stateMenuAnchor} open={Boolean(stateMenuAnchor)} onClose={closeStateMenu}
        PaperProps={{ sx: { minWidth: 160 } }}>
        {STATE_OPTIONS.map(opt => (
          <MenuItem key={opt.value} dense
            selected={stateMenuAppt?.state === opt.value}
            onClick={() => handleStateChange(opt.value)}>
            <Chip label={opt.label} size="small" sx={{ bgcolor: opt.color, color: 'white', fontWeight: 700, pointerEvents: 'none', mr: 0.5 }} />
          </MenuItem>
        ))}
      </Menu>

      {/* Check-in dialog */}
      <Dialog open={!!checkinAppt} onClose={() => setCheckinAppt(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Recepcionar paciente</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>{checkinAppt?.patient?.name}</strong>
            <br />
            <Typography component="span" variant="body2" color="text.secondary">
              Turno {checkinAppt ? new Date(checkinAppt.start_time + 'Z').toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }) : ''} — {checkinAppt?.doctor?.name}
            </Typography>
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <TextField
            label="Nota de recepción (opcional)"
            placeholder="Ej: trae estudios, tiene acompañante..."
            fullWidth
            multiline
            rows={2}
            value={checkinNote}
            onChange={(e) => setCheckinNote(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheckinAppt(null)}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={checkinLoading ? <CircularProgress size={16} /> : <HowToReg />}
            onClick={handleCheckin}
            disabled={checkinLoading}
          >
            Recepcionar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancellation reason dialog */}
      <Dialog open={!!cancelAppt} onClose={() => setCancelAppt(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancelar turno</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>{cancelAppt?.patient?.name}</strong>
            <br />
            <Typography component="span" variant="body2" color="text.secondary">
              Turno {cancelAppt ? new Date(cancelAppt.start_time + 'Z').toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }) : ''} — {cancelAppt?.doctor?.name}
            </Typography>
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <TextField
            label="Motivo de cancelación (opcional)"
            placeholder="Ej: Paciente reprogramó, no disponibilidad del médico..."
            fullWidth
            multiline
            rows={2}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelAppt(null)}>Volver</Button>
          <Button
            variant="contained"
            color="error"
            startIcon={changingState === cancelAppt?.id ? <CircularProgress size={16} /> : <Cancel />}
            onClick={confirmCancellation}
            disabled={changingState === cancelAppt?.id}
          >
            Confirmar cancelación
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancelación en lote */}
      <Dialog open={bulkCancelOpen} onClose={() => !bulkCancelling && setBulkCancelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancelar {selectedIds.length} turno{selectedIds.length === 1 ? '' : 's'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            El mismo motivo se va a aplicar a los {selectedIds.length} turnos seleccionados.
          </Typography>
          <TextField
            label="Motivo de cancelación (opcional)"
            placeholder="Ej: El médico no viene hoy..."
            fullWidth
            multiline
            rows={2}
            value={bulkCancelReason}
            onChange={(e) => setBulkCancelReason(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkCancelOpen(false)} disabled={bulkCancelling}>Volver</Button>
          <Button
            variant="contained"
            color="error"
            startIcon={bulkCancelling ? <CircularProgress size={16} /> : <Cancel />}
            onClick={confirmBulkCancel}
            disabled={bulkCancelling}
          >
            Cancelar {selectedIds.length} turno{selectedIds.length === 1 ? '' : 's'}
          </Button>
        </DialogActions>
      </Dialog>

      <SelfUpdateLinkDialog
        open={newPatientLinkOpen}
        onClose={() => setNewPatientLinkOpen(false)}
        patientId={null}
        patientPhone={null}
      />
    </Box>
  );
};

export default RecepcionistDashboard;
